import { FAIXA_COMERCIAL, ENQUADRAMENTOS } from './constantes'
export const formatCurrency = (value, currency = 'BRL', locale = 'pt-BR') => {

  if (value === null || value === undefined || value === '') {
    return 'R$ 0,00';
  }
  const numValue = typeof value === 'string' ? Number.parseFloat(value.replaceAll(/[^\d,-]/g, '').replace(',', '.')) : value;

  if (Number.isNaN(numValue)) {
    return 'R$ 0,00';
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numValue);
};

export const formatDecimal = (value, decimalPlaces = 2, locale = 'pt-BR') => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }
  const numValue = typeof value === 'string' ? Number.parseFloat(value.replaceAll(/[^\d,-]/g, '').replace(',', '.')) : value;

  if (Number.isNaN(numValue)) {
    return '0';
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces
  }).format(numValue);
};

// Formata um número como decimal BR sem separador de milhar (ex.: 1234.5 -> "1234,50").
// Usado nos campos de valor do Simulador Flex (Ato/E2/E3), que guardam o número "cru" em
// formData e não usam formatDecimal (que adicionaria separador de milhar).
export const formatToBrazilianDecimal = (value) => {
  return value.toFixed(2).replace('.', ',');
};

export const formatDate = (date) => {
  if (!date) return '';

  const dateObj = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(dateObj.getTime())) {
    return '';
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();

  return `${day}/${month}/${year}`;
};

export const formatCpf = (value) => {
  if (!value) return '';
  
  const numericValue = value.replaceAll(/\D/g, '');
  
  if (numericValue.length <= 3) {
    return numericValue;
  } else if (numericValue.length <= 6) {
    return `${numericValue.slice(0, 3)}.${numericValue.slice(3)}`;
  } else if (numericValue.length <= 9) {
    return `${numericValue.slice(0, 3)}.${numericValue.slice(3, 6)}.${numericValue.slice(6)}`;
  } else {
    return `${numericValue.slice(0, 3)}.${numericValue.slice(3, 6)}.${numericValue.slice(6, 9)}-${numericValue.slice(9, 11)}`;
  }
};

export const validateCpf = (cpf) => {
    if (typeof cpf !== 'string' || !cpf)
        return false;
 
    const numericCpf = cpf.replaceAll(/\D/g, ''); 
 
    if (numericCpf.length !== 11)
        return false;
 
    if (/^(\d)\1{10}$/.test(numericCpf))
        return false;
 
    const calculateDigit = (cpf, factor) => {
        let sum = 0;
        for (let i = 0; i < factor - 1; i++) {
            sum += Number.parseInt(cpf.charAt(i)) * (factor - i);
        }
        const digit = 11 - (sum % 11);
        return digit >= 10 ? 0 : digit;
    };
 
    const firstDigit = calculateDigit(numericCpf, 10);
    const secondDigit = calculateDigit(numericCpf, 11);
 
    return (
        firstDigit === Number.parseInt(numericCpf.charAt(9)) &&
        secondDigit === Number.parseInt(numericCpf.charAt(10)));
};

export const formatPercentage = (value) => {
  if (value === null || value === undefined) return '-';

  let numValue;
  if (typeof value === 'string') {
    numValue = Number.parseFloat(value.replace(',', '.'));
  } else {
    numValue = value;
  }

  if (Number.isNaN(numValue)) return '-';

  const percentValue = numValue * 100;
  return percentValue.toFixed(2).replace('.', ',') + '%';
};

export const calculateAge = (birthDateStr, currentDate = new Date()) => {
  if (!birthDateStr) {
    return null;
  }

  const birthDate = new Date(birthDateStr);

  if (Number.isNaN(birthDate.getTime())) {
    return null;
  }

  const birthYear = birthDate.getFullYear();
  const birthMonth = birthDate.getMonth();
  const birthDay = birthDate.getDate();

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const currentDay = currentDate.getDate();

  let age = currentYear - birthYear;

  if (
    currentMonth < birthMonth ||
    (currentMonth === birthMonth && currentDay < birthDay)
  ) {
    age--;
  }

  return age >= 0 ? age : null;
}

/**
 * Helper function to find and return enrollment ID by name
 * @param {string} nome - Name of the enrollment to find
 * @returns {string|null} - ID of the enrollment or null if not found
 */
const getEnrollmentIdByName = (nome) => {
  const enrollment = ENQUADRAMENTOS.find(e => e.nome === nome);
  return enrollment ? enrollment.id : null;
};

/**
 * Gets enrollment name for income <= 4700
 */
const getEnrollmentNameForLowIncome = (faixaEnum) => {
  if (faixaEnum === FAIXA_COMERCIAL.SBPE) return "SBPE - TR";
  if (faixaEnum === FAIXA_COMERCIAL.FAIXA4) return "PMCMV 4";
  if (faixaEnum === FAIXA_COMERCIAL.FAIXA3) return "CCFGTS/PMCMV 3";
  return "CCFGTS/PMCMV 2";
};

/**
 * Gets enrollment name for income <= 8600
 */
const getEnrollmentNameForMidIncome = (faixaEnum) => {
  if (faixaEnum === FAIXA_COMERCIAL.SBPE) return "SBPE - TR";
  if (faixaEnum === FAIXA_COMERCIAL.FAIXA4) return "PMCMV 4";
  return "CCFGTS/PMCMV 3";
};

/**
 * Gets enrollment name based on income range and commercial tier
 * @param {number} renda - Income amount
 * @param {number} faixaEnum - Commercial tier enum value
 * @returns {string} - The enrollment name
 */
const getEnrollmentNameByIncomeAndTier = (renda, faixaEnum) => {
  // Case 1: Renda <= 2850 e faixa FAIXA2E3
  if (renda <= 2850.00 && faixaEnum === FAIXA_COMERCIAL.FAIXA2E3) {
    return "CCFGTS/PMCMV 1";
  }
  
  // Case 2: Renda <= 4700
  if (renda <= 4700.00) {
    return getEnrollmentNameForLowIncome(faixaEnum);
  }
  
  // Case 3: Renda <= 8600
  if (renda <= 8600.00) {
    return getEnrollmentNameForMidIncome(faixaEnum);
  }
  
  // Case 4: Renda <= 12000 e nao SBPE
  if (renda <= 12000.00 && faixaEnum !== FAIXA_COMERCIAL.SBPE) {
    return "PMCMV 4";
  }
  
  // Case 5: Default - qualquer outro caso
  return "SBPE - TR";
};

/**
 * Gets the enrollment IDs based on commercial tier type and income
 * Refactored to reduce cognitive complexity while maintaining the exact same business logic
 */
export const getEnrollmentIdsByCommercialTierType = (faixaComercialTexto, renda = null) => {
  const faixaEnum = FAIXA_COMERCIAL[faixaComercialTexto] ?? FAIXA_COMERCIAL.NA;

  if (faixaEnum === FAIXA_COMERCIAL.NA) {
    return null;
  }

  const r = Number.parseFloat(renda);
  const enrollmentName = getEnrollmentNameByIncomeAndTier(r, faixaEnum);
  return getEnrollmentIdByName(enrollmentName);
};

/**
 * Converts a Brazilian currency string to a numeric value
 * @param {string|number} value - The currency string (e.g., "R$ 50.000,00") or number
 * @returns {number} - The numeric value
 */
export const parseCurrency = (value) => {
  if (!value || value === '') return 0;
  
  // If already a number, return it
  if (typeof value === 'number') return value;
  
  // Remove currency symbols, dots (thousands separator) and convert comma to decimal point
  const numericValue = value
    .replaceAll(/[R$\s]/g, '')  // Remove R$ and spaces
    .replaceAll('.', '')       // Remove dots (thousands separator)
    .replace(',', '.');       // Replace comma with dot (decimal separator)
  
  const parsed = Number.parseFloat(numericValue);
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * Normalizes tipo fluxo value from text format to numeric format
 * @param {string|number} tipo - The tipo fluxo value (e.g., "cem_zero" or "1")
 * @returns {string} - The normalized numeric value as string
 */
export const normalizeTipoFluxo = (tipo) => {
  if (!tipo) return '0';
  
  // Mapear valores textuais para numéricos
  const tipoFluxoMap = {
    'cem_zero': '1',
    'vinte_oitenta': '2',
    'trinta_setenta': '3',
    'quarenta_sessenta': '4',
    'cinquenta_cinquenta': '5',
    'trintaecinco_sessentaecinco': '6',
    'quarentaecinco_cinquentaecinco': '7'
  };
  
  return tipoFluxoMap[tipo?.toLowerCase()] || tipo;
};

/**
 * Lê uma propriedade de uma resposta de API que pode vir serializada em
 * camelCase ou PascalCase, dependendo do endpoint/serializador do backend.
 * @param {object} source - Objeto de origem (ex.: simulationResult, proposta)
 * @param {string} propertyName - Nome da propriedade em camelCase
 * @returns {*} O valor da propriedade em camelCase, ou em PascalCase como fallback
 */
export const getResponseProperty = (source, propertyName) => {
  const pascalCaseName = `${propertyName.charAt(0).toUpperCase()}${propertyName.slice(1)}`;
  return source?.[propertyName] ?? source?.[pascalCaseName];
};

/**
 * Formata o prazo de obra com data calculada e AMP
 * @param {number} prosolutoA - Número de meses do prazo de obra
 * @param {number} acompanhamentoMensalProducao - Percentual de AMP (0 a 1)
 * @returns {string} Formatted string: "19 - Ago/2027 (88% AMP)"
 */
export const formatPrazoDeObra = (prosolutoA, acompanhamentoMensalProducao) => {
  if (prosolutoA == null) return '-';
  
  const amp = acompanhamentoMensalProducao 
    ? (acompanhamentoMensalProducao * 100).toFixed(0) 
    : '0';
  
  // Calcular data: hoje + prosolutoA meses
  const hoje = new Date();
  const dataFutura = new Date(hoje.getFullYear(), hoje.getMonth() + prosolutoA, 1);
  
  // Formatar data como "Mês/Ano" (ex: Ago/2027)
  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const mesAbreviado = meses[dataFutura.getMonth()];
  const ano = dataFutura.getFullYear();
  
  return `${prosolutoA} - ${mesAbreviado}/${ano} (${amp}% AMP)`;
};